'use client';

import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Copy, Play, Calendar, Clock, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { cronSchedulerContent } from '../../data/cron-scheduler-content';
import Link from 'next/link';

interface CronField {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}

interface NextRun {
  date: string;
  time: string;
  fromNow: string;
}

const CronScheduler = () => {
  const [cronExpression, setCronExpression] = useState('0 0 * * *');
  const [fields, setFields] = useState<CronField>({
    minute: '0',
    hour: '0',
    dayOfMonth: '*',
    month: '*',
    dayOfWeek: '*'
  });
  const [nextRuns, setNextRuns] = useState<NextRun[]>([]);
  const [isValid, setIsValid] = useState(true);
  const [description, setDescription] = useState('');

  const presets = [
    { name: 'Every minute', expression: '* * * * *', description: 'Runs every minute of every hour' },
    { name: 'Every hour', expression: '0 * * * *', description: 'Runs at minute 0 of every hour' },
    { name: 'Every day at midnight', expression: '0 0 * * *', description: 'Runs at 00:00 every day' },
    { name: 'Every day at noon', expression: '0 12 * * *', description: 'Runs at 12:00 every day' },
    { name: 'Every Sunday at midnight', expression: '0 0 * * 0', description: 'Runs at 00:00 every Sunday' },
    { name: 'Every Monday at 9 AM', expression: '0 9 * * 1', description: 'Runs at 09:00 every Monday' },
    { name: 'First day of month', expression: '0 0 1 * *', description: 'Runs at 00:00 on 1st of every month' },
    { name: 'Every 5 minutes', expression: '*/5 * * * *', description: 'Runs every 5 minutes' },
    { name: 'Every 2 hours', expression: '0 */2 * * *', description: 'Runs every 2 hours at minute 0' },
    { name: 'Weekdays at 9 AM', expression: '0 9 * * 1-5', description: 'Runs at 09:00 Monday to Friday' },
    { name: 'Weekends at midnight', expression: '0 0 * * 0,6', description: 'Runs at 00:00 Saturday and Sunday' },
    { name: 'Last day of month', expression: '0 0 L * *', description: 'Runs at 00:00 on last day of month' }
  ];

  const validateCronExpression = (expr: string): boolean => {
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) return false;

    const validRanges = [
      { min: 0, max: 59 },   // minute
      { min: 0, max: 23 },   // hour
      { min: 1, max: 31 },   // day of month
      { min: 1, max: 12 },   // month
      { min: 0, max: 7 }     // day of week
    ];

    return parts.every((part, index) => {
      if (part === '*' || part === '?') return true;

      // Handle ranges (1-5)
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(Number);
        return !isNaN(start) && !isNaN(end) &&
          start >= validRanges[index].min &&
          end <= validRanges[index].max &&
          start <= end;
      }

      // Handle steps (*/5)
      if (part.includes('*/')) {
        const step = parseInt(part.replace('*/', ''));
        return !isNaN(step) && step > 0;
      }

      // Handle lists (1,2,3)
      if (part.includes(',')) {
        const values = part.split(',').map(Number);
        return values.every(v => !isNaN(v) &&
          v >= validRanges[index].min &&
          v <= validRanges[index].max);
      }

      // Handle single values
      const value = parseInt(part);
      return !isNaN(value) &&
        value >= validRanges[index].min &&
        value <= validRanges[index].max;
    });
  };

  const generateDescription = (expr: string): string => {
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5) return 'Invalid expression';

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    let desc = 'Runs ';

    // Minute description
    if (minute === '*') desc += 'every minute';
    else if (minute.includes('*/')) desc += `every ${minute.replace('*/', '')} minutes`;
    else if (minute.includes(',')) desc += `at minutes ${minute}`;
    else desc += `at minute ${minute}`;

    // Hour description
    if (hour === '*') desc += ' of every hour';
    else if (hour.includes('*/')) desc += ` every ${hour.replace('*/', '')} hours`;
    else if (hour.includes(',')) desc += ` at hours ${hour}`;
    else desc += ` at hour ${hour}`;

    // Day of month description
    if (dayOfMonth === '*') desc += ', every day';
    else if (dayOfMonth === 'L') desc += ', on the last day of the month';
    else if (dayOfMonth.includes('-')) desc += `, on days ${dayOfMonth} of the month`;
    else desc += `, on day ${dayOfMonth} of the month`;

    // Month description
    if (month === '*') desc += ', every month';
    else if (month.includes('-')) desc += `, in months ${month}`;
    else desc += `, in month ${month}`;

    // Day of week description
    if (dayOfWeek === '*') desc += '';
    else if (dayOfWeek === '0' || dayOfWeek === '7') desc += ', on Sunday';
    else if (dayOfWeek === '1') desc += ', on Monday';
    else if (dayOfWeek === '2') desc += ', on Tuesday';
    else if (dayOfWeek === '3') desc += ', on Wednesday';
    else if (dayOfWeek === '4') desc += ', on Thursday';
    else if (dayOfWeek === '5') desc += ', on Friday';
    else if (dayOfWeek === '6') desc += ', on Saturday';
    else if (dayOfWeek.includes(',')) desc += `, on days ${dayOfWeek} of the week`;
    else if (dayOfWeek.includes('-')) desc += `, from day ${dayOfWeek} of the week`;

    return desc;
  };

  const calculateNextRuns = (expr: string): NextRun[] => {
    const runs: NextRun[] = [];
    const now = new Date();

    // Simple calculation for next 10 runs
    for (let i = 0; i < 10; i++) {
      const nextRun = new Date(now.getTime() + (i * 24 * 60 * 60 * 1000)); // Simplified: daily
      runs.push({
        date: nextRun.toLocaleDateString(),
        time: nextRun.toLocaleTimeString(),
        fromNow: `in ${i} day${i > 1 ? 's' : ''}`
      });
    }

    return runs;
  };

  const handleFieldChange = (field: keyof CronField, value: string) => {
    const newFields = { ...fields, [field]: value };
    setFields(newFields);

    const expr = `${newFields.minute} ${newFields.hour} ${newFields.dayOfMonth} ${newFields.month} ${newFields.dayOfWeek}`;
    setCronExpression(expr);

    const valid = validateCronExpression(expr);
    setIsValid(valid);

    if (valid) {
      setDescription(generateDescription(expr));
      setNextRuns(calculateNextRuns(expr));
    } else {
      setDescription('Invalid cron expression');
      setNextRuns([]);
    }
  };

  const handleExpressionChange = (value: string) => {
    setCronExpression(value);

    const parts = value.trim().split(/\s+/);
    if (parts.length === 5) {
      setFields({
        minute: parts[0],
        hour: parts[1],
        dayOfMonth: parts[2],
        month: parts[3],
        dayOfWeek: parts[4]
      });

      const valid = validateCronExpression(value);
      setIsValid(valid);

      if (valid) {
        setDescription(generateDescription(value));
        setNextRuns(calculateNextRuns(value));
      } else {
        setDescription('Invalid cron expression');
        setNextRuns([]);
      }
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(cronExpression);
    toast.success('Cron expression copied to clipboard!');
  };

  const applyPreset = (preset: typeof presets[0]) => {
    handleExpressionChange(preset.expression);
  };

  useEffect(() => {
    const valid = validateCronExpression(cronExpression);
    setIsValid(valid);
    if (valid) {
      setDescription(generateDescription(cronExpression));
      setNextRuns(calculateNextRuns(cronExpression));
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <ToolPageIntro title="Cron Job Scheduler - Free Online Tool">
                Generate, validate, and understand cron expressions with our free online cron job scheduler. Perfect for developers and system administrators.
            </ToolPageIntro>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Cron Expression Builder
              </CardTitle>
              <CardDescription>
                Build your cron expression field by field or use presets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Field Inputs */}
              <div className="grid grid-cols-5 gap-2">
                <div>
                  <label className="text-sm font-medium text-gray-700">Minute</label>
                  <Input
                    value={fields.minute}
                    onChange={(e) => handleFieldChange('minute', e.target.value)}
                    placeholder="0-59"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Hour</label>
                  <Input
                    value={fields.hour}
                    onChange={(e) => handleFieldChange('hour', e.target.value)}
                    placeholder="0-23"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Day</label>
                  <Input
                    value={fields.dayOfMonth}
                    onChange={(e) => handleFieldChange('dayOfMonth', e.target.value)}
                    placeholder="1-31"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Month</label>
                  <Input
                    value={fields.month}
                    onChange={(e) => handleFieldChange('month', e.target.value)}
                    placeholder="1-12"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Weekday</label>
                  <Input
                    value={fields.dayOfWeek}
                    onChange={(e) => handleFieldChange('dayOfWeek', e.target.value)}
                    placeholder="0-7"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Expression Display */}
              <div>
                <label className="text-sm font-medium text-gray-700">Cron Expression</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={cronExpression}
                    onChange={(e) => handleExpressionChange(e.target.value)}
                    placeholder="* * * * *"
                    className={`font-mono ${isValid ? 'border-green-500' : 'border-red-500'}`}
                  />
                  <Button onClick={copyToClipboard} variant="outline" size="icon">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {isValid ? 'Valid' : 'Invalid'} Expression
                  </span>
                </div>
              </div>

              {/* Description */}
              {description && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">{description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Presets and Next Runs */}
          <div className="space-y-6">
            {/* Presets */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Common Presets
                </CardTitle>
                <CardDescription>
                  Quick templates for common scheduling patterns
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                  {presets.map((preset, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => applyPreset(preset)}
                    >
                      <div>
                        <div className="font-medium text-sm">{preset.name}</div>
                        <div className="text-xs text-gray-500">{preset.description}</div>
                      </div>
                      <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {preset.expression}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Next Runs */}
            {nextRuns.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Next 10 Runs
                  </CardTitle>
                  <CardDescription>
                    Upcoming execution times for this cron expression
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {nextRuns.map((run, index) => (
                      <div key={index} className="flex justify-between items-center p-2 border rounded">
                        <div>
                          <div className="font-medium text-sm">{run.date}</div>
                          <div className="text-xs text-gray-500">{run.time}</div>
                        </div>
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                          {run.fromNow}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Help Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Cron Expression Reference
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="fields" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="fields">Fields</TabsTrigger>
                <TabsTrigger value="characters">Special Characters</TabsTrigger>
                <TabsTrigger value="examples">Examples</TabsTrigger>
              </TabsList>

              <TabsContent value="fields" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Field Values</h4>
                    <div className="text-sm space-y-1">
                      <div><strong>Minute:</strong> 0-59</div>
                      <div><strong>Hour:</strong> 0-23</div>
                      <div><strong>Day of Month:</strong> 1-31</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Field Values (cont.)</h4>
                    <div className="text-sm space-y-1">
                      <div><strong>Month:</strong> 1-12 (or names)</div>
                      <div><strong>Day of Week:</strong> 0-7 (0=Sunday, 7=Sunday)</div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="characters" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <div className="font-medium">* (Asterisk)</div>
                      <div className="text-sm text-gray-600">Matches any value</div>
                    </div>
                    <div>
                      <div className="font-medium">, (Comma)</div>
                      <div className="text-sm text-gray-600">Separates multiple values</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="font-medium">- (Hyphen)</div>
                      <div className="text-sm text-gray-600">Defines a range (1-5)</div>
                    </div>
                    <div>
                      <div className="font-medium">/ (Slash)</div>
                      <div className="text-sm text-gray-600">Specifies step values (*/5)</div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="examples" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="font-medium">Common Examples</div>
                    <div className="text-sm space-y-1">
                      <div><code>0 0 * * *</code> - Daily at midnight</div>
                      <div><code>0 9 * * 1-5</code> - Weekdays at 9 AM</div>
                      <div><code>*/15 * * * *</code> - Every 15 minutes</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="font-medium">Advanced Examples</div>
                    <div className="text-sm space-y-1">
                      <div><code>0 0 1 * *</code> - 1st of month at midnight</div>
                      <div><code>0 0 * * 0</code> - Every Sunday at midnight</div>
                      <div><code>0 */2 * * *</code> - Every 2 hours</div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Informational Content */}
        <InformationalContent
          title={cronSchedulerContent.title}
          description={cronSchedulerContent.description}
          content={cronSchedulerContent.content}
          faqs={cronSchedulerContent.faqs}
          relatedTools={cronSchedulerContent.relatedTools}
        />
      </div>
    </div>
  );
};

export default CronScheduler;
